import React, { useContext, useState } from 'react';
import { ILocalizationContext, LocalizationContext } from '../../localizationState/localizationContext';
import { ICustomerLocalizationItem } from 'types';
import { useTranslation } from 'react-i18next';
import { NewButton } from 'components/Button/NewButton/NewButton';
import { Icon, IconName } from 'components/Icon/Icon';
import { OutsideFormInputNew } from 'components/Input/OutsideFormInputNew';
import Text from 'components/Text/Text';

interface IProps {
	groupId: Nullable<string>;
	item: ICustomerLocalizationItem;
	languageId: string;
}

export const LocalizationItem: React.FC<IProps> = ({ groupId, item, languageId }) => {
	const { t } = useTranslation('form');
	const { t: tLocalization } = useTranslation('localizationSettings');
	const { saveTranslation } = useContext<ILocalizationContext>(LocalizationContext);
	const savedValue = item.translations.find((el) => el.languageId === languageId)?.value ?? '';
	const [isEditing, setIsEditing] = useState<boolean>(false);
	const [value, setValue] = useState<string>(savedValue);

	const handleEdit = () => {
		setValue(savedValue);
		setIsEditing(true);
	};

	const handleSave = async () => {
		const isSaved = await saveTranslation({ groupId, itemKey: item.key, languageId, value });

		if (isSaved) {
			setIsEditing(false);
		}
	};

	if (isEditing) {
		return (
			<div className="flex items-end gap-2" data-test="LocalizationItem">
				<OutsideFormInputNew
					testId="TranslationInput"
					name={`${item.key}-${languageId}`}
					label={tLocalization(item.label || '')}
					value={value}
					onChange={setValue}
				/>
				<NewButton
					color="green"
					size="small"
					icon={true}
					iconLeft={<Icon name={IconName.done} size="lg" />}
					ariaLabel={t('saveButton')}
					testId="SaveTranslationBtn"
					onClick={handleSave}
				/>
			</div>
		);
	}

	return (
		<div className="flex items-end gap-2" data-test="LocalizationItem">
			<div className="min-w-0 flex-1 pb-2 border-0 border-b border-solid border-grey-03-new flex flex-col gap-1">
				<Text
					translationNameSpace="localizationSettings"
					translationKey={item.label}
					theme="neutralMedium"
					size="xs"
				/>
				<Text translationKey={savedValue} theme="neutralDark" size="m" withTooltip={true} isNewTooltip={true} />
			</div>
			<NewButton
				variant="shadow"
				size="small"
				icon={true}
				iconLeft={<Icon name={IconName.edit} size="lg" />}
				ariaLabel={t('editButton')}
				testId="EditTranslationBtn"
				onClick={handleEdit}
			/>
		</div>
	);
};
